import xhrService from '../xhrService';
import { formatISO } from 'date-fns';
import db from '../../db/db';
import { random } from 'lodash';
import { getDateOnly } from '../../utils/date'
export const baseUrl = '/api/inspections';
const getUrl = (id) => `${baseUrl}/${id}`;

const errors = {
  'inspection': 'All required inspection questions must be answered.',
  'acknowledgement': 'All inspector signature(s) are required, before any acknowledgement signature(s) may be submitted.',
  'compliance': 'All inspector signature(s) are required, before any compliance signature(s) may be submitted.'
}


const updateOfflineInspection = async (id, data, online) => {
  const numericId = Number(id);

  // Helper function to fetch data from database
  const fetchFromDb = async (table) => {
    try {
      return await db[table].get(numericId);
    } catch (e) {
      console.log("error", e);
      throw e;
    }
  };

  // Helper function to update database
  const updateDb = async (table, d) => {
    try {
      return await db[table].update(numericId, d);
    } catch (e) {
      console.log("error", e);
      throw e;
    }
  };

  // Fetch all required data in parallel
  const [questions, inspection, certification] = await Promise.all([
    fetchFromDb('questions'),
    fetchFromDb('inspections'),
    fetchFromDb('certifications')
  ]);

  // Map question IDs to answers
  const answersMap = new Map();

  for (const key in data) {
    const questionId = Number(key.split('-')[1]) ? Number(key.split('-')[1]) : Number(key)

    answersMap.set(questionId, data[key]);
  }

  // Process questions and collect unanswered required questions
  const unansweredAndRequired = [];

  let inspection_types = inspection.inspection_types || [];

  let inspectionDate = inspection.created_date;

  questions.template.question_groups.forEach(group => {
    group.questions.forEach(question => {
      if (answersMap.has(question.id)) {
        // Update inspection types if this is the type question
        if (question.input_label === 'Inspection Type') {
          const newType = answersMap.get(question.id);
          // If it's not already in the array, add it
          if (newType && !inspection_types.map((inspection_types) => inspection_types.type).includes(newType)) {
            inspection_types = [...inspection_types, { type: newType }];
          }
        }
        if (question.features.includes('isInspectionDate')) {
          inspectionDate = getDateOnly(new Date(answersMap.get(question.id)));
        }

        // Check if required question is unanswered
        if (question.features.includes('required') && answersMap.get(question.id) === '') {
          unansweredAndRequired.push(question);
        }

        // Update the answer
        question.answer = answersMap.get(question.id);
      }
    });
  });

  // Update timestamp
  if (!online) {
    questions.updated_at = new Date();
    questions.sync = {
      method: 'PUT',
      payload: data,
      synced: false,
    }
  }
  // Determine inspection status
  const status = unansweredAndRequired.length ? "started" : "answered";

  // Update questions in database
  await updateDb('questions', questions);

  // Update inspection status and inspection_types
  await updateDb('inspections', { created_date: inspectionDate, status, inspection_types });

  // Update certification if inspection is complete
  if (status === 'answered') {
    certification.groupOptions.inspection.errors = [];
    certification.groupOptions.inspection.enabled = true;
    certification.inspectionDOI = inspectionDate;
    certification.minCertificationDate = inspectionDate;
    await updateDb('certifications', certification);
  }

  // Return updated data
  return {
    inspNumber: inspection.inspectionNumber,
    template: questions.template,
    inspection: { ...inspection, status, inspection_types, created_date: inspectionDate },
    templateUnanswered: {
      status,
      unansweredAndRequired
    }
  };
};

const createOfflineQuestionComment = async (questionId, inspectionId, data) => {
  const question = await db.questions.get(Number(inspectionId)).catch((e) => {
    console.log('error', e);
    throw e;
  });

  if (!question) {
    throw new Error('Question not found');
  }
  if (!question.template.question_groups) {
    throw new Error('Question groups not found');
  }

  const questionData = question.template.question_groups.map((group) => {
    return {
      ...group,
      questions: group.questions.map((q) => {
        if (q.id === Number(questionId)) {
          q.comment = data.comment || '';
        }

        return q;
      }),
    };
  });

  if (data.shouldDelete) {
    await db.commentPayload
      .where('inspection_id')
      .equals(Number(inspectionId))
      .and((payload) => payload.question_id === Number(questionId))
      .delete()

  } else {
    await db.commentPayload
      .add({
        inspection_id: Number(inspectionId),
        question_id: Number(questionId),
        sync: {
          method: 'POST',
          payload: data,
          synced: false,
        },
      })
  }

  await db.questions
    .update(Number(inspectionId), {
      template: {
        ...question.template,
        question_groups: questionData,
      },
    })

  const newQuestions = await db.questions
    .get(Number(inspectionId))
    .catch((e) => {
      console.log('error', e);
      throw e;
    });

  return newQuestions.template;
};


export const inspectionApi = {

  get: async (id) => {
    const offline = navigator.onLine ? false : true;

    if (!offline) {
      return xhrService
        .get(getUrl(id))
        .then((res) => res.data)
        .catch((e) => {
          throw e;
        });
    } else {
      const data = await db.inspections.get(Number(id)).catch((e) => {
        console.log('error', e);
        throw e;
      });

      const findings = await db.findings
        .where('id')
        .anyOf(data.findings.map((f) => f.id))
        .toArray();

      data.findings = findings || data.findings;

      return { inspection: data };
    }
  }
  ,
  create: async (projectId, types, inspectionTypeId = null) => {

    const offline = navigator.onLine ? false : true;

    if (!offline) {
      const requestBody = {
        projectId,
        inspection_types: Array.isArray(types) ? types : [types],
      };

      // Add inspection_type_id if provided for carry over logic
      if (inspectionTypeId) {
        requestBody.inspection_type_id = inspectionTypeId;
      }

      return xhrService
        .post(baseUrl, requestBody)
        .then((res) => {
          return res.data;
        })
        .catch((e) => {
          throw e;
        });
    }
    else {
      const latestInspection = await db.inspections.where('project_id').equals(Number(projectId)).reverse().sortBy('inspectionNumber').catch((e) => {
        throw e
      });

      const project = await db.projects.get(Number(projectId))
      const user = await db.user.get(1)

      const projectInspectionUploads = await db.projectInspectionUploads
        .where('project_id')
        .equals(Number(projectId))
        .and(upload => upload.inspection_id === latestInspection[0]?.id)
        .toArray()

      // Get the latest inspection's template to check inspection_type_id
      let latestInspectionTemplate = null;
      if (latestInspection.length > 0) {
        latestInspectionTemplate = await db.inspectionTemplate.get(Number(latestInspection[0].template_id));
      } else {
        console.log('❌ No latest inspection found');
      }

      const shouldCarryOverFindings = latestInspection.length > 0 &&
        currentInspectionTemplate &&
        latestInspectionTemplate &&
        currentInspectionTemplate.inspection_type_id === latestInspectionTemplate.inspection_type_id;

      if (shouldCarryOverFindings) {
        const data = {
          inspectionNumber: latestInspection[0].inspectionNumber + 1,
          project_id: latestInspection[0].project_id,
          inspection_types: Array.isArray(types) ? types : [types],
          acknowledgement_date: null,
          created_at: new Date(),
          updated_at: new Date(),
          created_date: getDateOnly(new Date()),
          findings: latestInspection[0].findings.filter(
            (finding) => finding.date_completed === null,
          ),
          id: Number(latestInspection[0].id) + 100,
          inspector: latestInspection[0].inspector,
          inspection_template: latestInspection[0].inspection_template,
          project: latestInspection[0].project,
          status: 'new',
          template_id: latestInspection[0].template_id,
          updated_date: formatISO(new Date()),
          certification_date: null,
          compliance_date: null,
          certification_notes: null,
          sync: {
            method: 'POST', // POST, PUT, or DELETE
            payload: {
              projectId,
              inspection_types: Array.isArray(types) ? types : [types],
            },
            synced: false,
          },
        };

        const question = await db.questions
          .get(Number(latestInspection[0].id))
          .catch((e) => {
            console.log('error', e);
            throw e;
          });

        const certification = await db.certifications
          .get(Number(latestInspection[0].id))
          .catch((e) => {
            console.log('error', e);
            throw e;
          });

        Object.keys(certification.userSignatures).forEach((key) => {
          certification.userSignatures[key] = [];
          certification.groupOptions[key].enabled = false;
          if (certification.groupOptions[key].errors.length === 0) {
            certification.groupOptions[key].errors.push(errors[key]);
          }
        });

        //empty question answers and comments
        question.template.question_groups.forEach((group) => {
          group.questions.forEach((question) => {
            if (question.features.includes('carryOver')) {
              question.comment = '';
            }
            else if (question.features.includes('isInspectionDate', 'defaultToToday')) {
              question.answer = getDateOnly(new Date());
              question.comment = '';
            } else if (question.features.includes('defaultToProject')) {
              question.answer = project.name;
              question.comment = '';
            }
            else if (question.features.includes('defaultToCurrentUser')) {
              question.answer = user.name
              question.comment = '';
            }
            else {
              question.answer = '';
              question.comment = '';
            }
          });
        });

        await db.certifications
          .add({ ...certification, inspectionId: data.id })
          .catch((e) => {
          });

        await db.questions
          .add({ ...question, inspection_id: data.id })
          .catch((e) => {
            console.log('error', e);
          });

        await db.inspectionNotes
          .add({ inspection_id: data.id, notes: [], sync: { synced: true, payload: [] } })
          .catch((e) => {
            console.log('error', e);
          });

        if (projectInspectionUploads.length > 0) {
          await db.projectInspectionUploads.add({
            project_id: Number(projectId),
            inspection_id: data.id,
            data: projectInspectionUploads[0].data.map(uploads => {
              return {
                ...uploads,
                inspection_id: data.id,
              };
            })
          }).catch((e) => {
            console.log('error', e);
          })
        }

        await db.projects
          .update(projectId, {
            inspections: [
              ...project.inspections,
              {
                created_date: getDateOnly(new Date()),
                id: data.id,
                inspection_types: data.inspection_types,
              },
            ],
          })
          .catch((e) => {
            console.log('error', e);
            throw e;
          });

        const inspection = await db.inspections.add(data).catch((e) => {
          throw e;
        });

        return db.inspections.get(inspection).catch((e) => {
          throw e;
        });
      } else {
        const inspectionTemplate = await db.inspectionTemplate.get(
          Number(project.inspection_template_id),
        );

        if (!inspectionTemplate) {
          return Promise.reject(
            new Error('Inspection template not found for this project'),
          );
        }
        const data = {
          inspectionNumber: latestInspection.length
            ? latestInspection[0].inspectionNumber + 1
            : 1,
          project_id: projectId,
          inspection_types: Array.isArray(types) ? types : [types],
          acknowledgement_date: null,
          created_at: new Date(),
          updated_at: new Date(),
          created_date: getDateOnly(new Date()),
          findings: [],
          id: latestInspection.length ? Number(latestInspection[0].id) + 100 : random(false, 1000000),
          inspector: [],
          inspection_template: {
            has_acknowledgement:
              inspectionTemplate.template.has_acknowledgement,
            has_compliance_cert:
              inspectionTemplate.template.has_compliance_cert,
          },
          project: latestInspection.length
            ? latestInspection[0].project
            : { id: projectId, client: { disable_carry_overs: false } },
          status: 'new',
          template_id: project.inspection_template_id,
          updated_date: formatISO(new Date()),
          certification_date: null,
          compliance_date: null,
          certification_notes: null,
          sync: {
            method: 'POST', // POST, PUT, or DELETE
            payload: {
              projectId,
              inspection_types: Array.isArray(types) ? types : [types],
            },
            synced: false,
          },
        };

        const question = inspectionTemplate;
        const certifications = inspectionTemplate.signature;

        certifications.userSignatures = certifications.userSignatures || {};
        certifications.groupOptions = certifications.groupOptions || {};

        Object.keys(certifications.attestations).forEach((key) => {
          certifications.userSignatures[key] = [];

          certifications.groupOptions[key] = certifications.groupOptions[
            key
          ] || {
            enabled: false,
            errors: [],
          };

          if (certifications.groupOptions[key].errors.length === 0) {
            certifications.groupOptions[key].errors.push(errors[key]);
          }
        });

        //empty question answers and comments
        question.template.question_groups.forEach((group) => {
          group.questions.forEach((question) => {
            if (question.features.includes('carryOver')) {
              question.comment = '';
              question.answer = '';
            } else if (question.features.includes('isInspectionDate', 'defaultToToday')) {
              question.answer = getDateOnly(new Date());
              question.comment = '';
            } else if (question.features.includes('defaultToProject')) {
              question.answer = project.name;
              question.comment = '';
            }
            else if (question.features.includes('defaultToCurrentUser')) {
              question.answer = user.name
              question.comment = '';
            }
            else {
              question.answer = '';
              question.comment = '';
            }
          });
        });

        await db.certifications
          .add({ ...certifications, inspectionId: data.id, sync: { synced: true, payload: [] } })
          .catch((e) => {
            throw e;
          });

        await db.questions
          .add({
            inspNumber: data.inspectionNumber,
            template: question.template,
            inspection_id: data.id,
            questionTypes: question.questionTypes,
          })
          .catch((e) => {
            throw e;
          });

        await db.inspectionNotes
          .add({ inspection_id: data.id, notes: [], sync: { synced: true, payload: [] } })
          .catch((e) => {
            console.log('error', e);
            throw e;
          });

        if (projectInspectionUploads.length > 0) {
          await db.projectInspectionUploads.add({
            project_id: Number(projectId),
            inspection_id: data.id,
            data: projectInspectionUploads[0].data.map(uploads => {
              return {
                ...uploads,
                inspection_id: data.id,
              };
            })
          }).catch((e) => {
            console.log('error', e);
          })
        }

        await db.projects
          .update(projectId, {
            inspections: [
              ...project.inspections,
              {
                created_date: getDateOnly(new Date()),
                id: data.id,
                inspection_types: data.inspection_types,
              },
            ],
          })
          .catch((e) => {
            console.log('error', e);
            throw e;
          });

        const inspection = await db.inspections.add(data).catch((e) => {
          throw e;
        });

        return db.inspections.get(inspection).catch((e) => {
          throw e;
        });
      }
    }
  },
  createWithJunction: async (requestBody) => {
    // New method to handle the updated request format with junctionId
    const offline = navigator.onLine ? false : true;

    if (!offline) {
      return xhrService
        .post(baseUrl, requestBody)
        .then((res) => res.data)
        .catch((e) => {
          throw e;
        });
    } else {
      // For offline mode, fall back to the original create method
      return inspectionApi.create(requestBody.projectId, requestBody.inspection_types);
    }
  },
  update: async (id, data) => {

    if (navigator.onLine) {
      const updatedData = await xhrService.put(getUrl(id), data).then((res) => res.data);

      await updateOfflineInspection(id, data, true).catch((error) => {
        console.log('Error updating offline inspection', error);
      });

      return updatedData;
    }
    else {
      return await updateOfflineInspection(id, data, false);
    }
  },
  destroy: (id, reason) => xhrService.delete(getUrl(id), { data: { reason } }).then((res) => res.data),
  complete: (id, findings, dateCompleted, comments = undefined) => {
    const reqBody = {
      findings,
      dateCompleted,
    };

    if (comments) {
      reqBody.comments = comments;
    }

    return xhrService
      .put(`${baseUrl}/${id}/findings`, reqBody)
      .then((res) => res.data);
  },
  createQuestionComment: async (questionId, inspectionId, data) => {
    if (navigator.onLine) {
      return xhrService
        .post(`${baseUrl}/comment/${questionId}/${inspectionId}`, data)
        .then((res) => res.data);
    }
    else {
      return createOfflineQuestionComment(questionId, inspectionId, data);

    }
  },
  reassign: (inspectionId, userId, projectId, reassignMode = "primary") => {
    return xhrService
      .post(`${baseUrl}/${inspectionId}/inspector/${userId}`, {
        projectId: projectId,
        reassignMode: reassignMode, // "primary" or "next_only"
      })
      .then((res) => res.data);
  },
  reassignV2: (userId, projectIds, clientIds, reassignMode = "primary") => {
    return xhrService
      .post(`${baseUrl}/reassign/${userId}`, {
        projectIds: projectIds,
        clientIds: clientIds,
        reassignMode: reassignMode, // "primary" or "next_only"
      })
      .then((res) => res.data);
  },
  getInspectionLinkedAllData: (id) => {
    return xhrService
      .get(`/api/inspection/${id}`)
      .then((res) => res.data)
      .catch((error) => console.log(error));
  },

  getInspectionMonthWise: (projectId, date_range) => {
    return xhrService
      .get(
        `/api/inspections/getInspectionMonthWise/${projectId}?date_range[]=${date_range[0]}&date_range[]=${date_range[1]}`,
      )
      .then((res) => res.data)
      .catch((error) => console.log(error));
  },
  getInspectionUploads: (id) => {
    if (!navigator.onLine) {
      return db.inspectionUploads.get(id)
        .then((res) => {
          if (res) {
            return res.uploads;
          } else {
            return [];
          }
        }).catch((error) => {
          console.log(error);

          return [];
        });
    }
    else {
      return xhrService
        .get(`${baseUrl}/${id}/uploads`)
        .then((res) => res.data)
        .catch((error) => console.log(error));
    }

  },
  sortInspUploads: (id, order) => {
    console.log(order);

    return xhrService
      .put(`${baseUrl}/${id}/sortInspUploads`, {
        order: order,
      })
      .then((res) => res.data)
      .catch((error) => console.log(error));
  },
  cancelOverdue: (data) => {
    return xhrService
      .post(`${baseUrl}/cancel-overdue`, data)
      .then((res) => res.data)
      .catch((error) => {
        console.log(error);
        throw error;
      });
  },
  getTenantBackdatingDetails: () => {
    return xhrService
      .get(`${baseUrl}/getTenantBackdatingDetails`)
      .then((res) => res.data)
      .catch((error) => {
        console.log(error);
        throw error;
      });
  }
};

export default inspectionApi;